import React, { useRef, useState, useEffect } from 'react';
import {
  Plus,
  Trash2,
  Upload,
  Sparkles,
  ChevronDown,
  ChevronUp,
  User,
  Briefcase,
  FileText,
  ToggleLeft,
  ToggleRight,
  Save,
  Layout,
  Hash,
  CheckCircle2,
  AlertCircle,
  Activity,
  X,
  ShieldAlert,
  Info,
} from 'lucide-react';
import type { InvoiceData, InvoiceItem, InvoiceSettings } from '../../types';
import { generateInvoiceFromText } from '../../services/openaiService';
import { logger, SystemHealth } from '../../services/logger';
import { APP_CONFIG } from '../../config';

interface Props {
  data: InvoiceData;
  settings: InvoiceSettings;
  onUpdate: (data: Partial<InvoiceData>) => void;
  onSettingsChange: (settings: InvoiceSettings) => void;
  onManualSave: () => void;
}

const Section: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}> = ({ title, icon, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[2rem] overflow-hidden shadow-sm mb-6 transition-all duration-300">
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        className="w-full flex items-center justify-between p-6 bg-slate-50/50 dark:bg-slate-900/50 hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-all text-left focus:outline-none group"
      >
        <div className="flex items-center gap-4 font-black text-slate-900 dark:text-slate-100 tracking-tight">
          <span className="text-brand-500 group-hover:scale-110 transition-transform">{icon}</span>
          <span className="text-lg">{title}</span>
        </div>
        <div>
          {isOpen ? (
            <ChevronUp size={16} className="text-slate-400" />
          ) : (
            <ChevronDown size={16} className="text-slate-400" />
          )}
        </div>
      </button>
      {isOpen && (
        <div className="p-6 space-y-6 border-t border-slate-100 dark:border-slate-800 animate-in fade-in duration-300">
          {children}
        </div>
      )}
    </div>
  );
};

const InvoiceForm: React.FC<Props> = ({
  data,
  settings,
  onUpdate,
  onSettingsChange,
  onManualSave,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiInputText, setAiInputText] = useState('');
  const aiInputRef = useRef<HTMLTextAreaElement>(null);

  // Lazy AI health check — only on user request, not on mount
  useEffect(() => {
    // no-op: diagnostics are triggered manually via "System Integrity Check" button
  }, []);

  const handleSaveClick = () => {
    setSaveStatus('saving');
    onManualSave();
    setTimeout(() => {
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }, 600);
  };

  const handleRunTests = async () => {
    setIsTesting(true);
    try {
      const report = await logger.runDiagnostics();
      setHealth(report);
    } catch (e) {
      console.error('Diagnostics failed', e);
    } finally {
      setIsTesting(false);
    }
  };

  const validateField = (name: string, value: any) => {
    let error = '';
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      error = 'Required';
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const updateItem = (index: number, field: keyof InvoiceItem, value: any) => {
    const newItems = [...data.items];
    // For numeric fields (quantity, rate, tax), ensure positive values
    if (field === 'quantity' || field === 'rate' || field === 'tax') {
      const numValue = Number(value);
      value = isNaN(numValue) ? 0 : Math.abs(numValue);
    }
    newItems[index] = { ...newItems[index], [field]: value };
    onUpdate({ items: newItems });
  };

  const addItem = () => {
    const newItem: InvoiceItem = {
      id: crypto.randomUUID(),
      description: '',
      quantity: 1,
      rate: 0,
      tax: 0,
    };
    onUpdate({ items: [...data.items, newItem] });
  };

  const removeItem = (index: number) => {
    if (data.items.length <= 1) return;
    const newItems = data.items.filter((_, i) => i !== index);
    onUpdate({ items: newItems });
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (default 2MB)
    const maxSize = APP_CONFIG.limits.maxLogoSize;
    if (file.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
        logo: `Logo must be under ${Math.round(maxSize / 1024 / 1024)}MB`,
      }));
      e.target.value = '';
      return;
    }
    setErrors((prev) => ({ ...prev, logo: '' }));

    const reader = new FileReader();
    reader.onloadend = () => {
      onUpdate({ logo: reader.result as string });
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = (e: React.MouseEvent) => {
    e.stopPropagation();
    onUpdate({ logo: null });
  };

  const handleAIGenerate = async () => {
    const text = aiInputText.trim();
    if (!text) return;

    setIsGenerating(true);
    setAiError(null);
    try {
      const partialData = await generateInvoiceFromText(text);
      onUpdate(partialData);
      setAiInputText('');
      if (aiInputRef.current) aiInputRef.current.value = '';
      // Refresh health status after successful generation
      const report = await logger.runDiagnostics();
      setHealth(report);
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : 'Unknown error';
      if (errorMessage === 'API_KEY_MISSING') {
        setAiError('API key not configured. Add VITE_OPENAI_API_KEY to your .env file.');
      } else {
        setAiError(`AI synthesis failed: ${errorMessage}`);
      }
      logger.error('AI Generation failed', { error: errorMessage });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-6 pb-12">
      <div className="flex items-center justify-between px-2">
        <div className="flex items-center gap-4">
          <button
            onClick={() => onSettingsChange({ ...settings, autoSave: !settings.autoSave })}
            className="flex items-center gap-2 group"
          >
            {settings.autoSave ? (
              <ToggleRight className="text-brand-500" size={28} />
            ) : (
              <ToggleLeft className="text-slate-300 dark:text-slate-600" size={28} />
            )}
            <div className="flex flex-col items-start">
              <span
                className={`text-[10px] font-black uppercase tracking-widest ${settings.autoSave ? 'text-brand-500' : 'text-slate-400'}`}
              >
                {settings.autoSave ? 'Auto-Sync Active' : 'Manual Mode'}
              </span>
            </div>
          </button>

          {settings.autoSave && (
            <div className="flex items-center gap-1.5 text-[9px] font-bold text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded-full animate-pulse">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
              Live
            </div>
          )}
        </div>

        <button
          onClick={handleSaveClick}
          disabled={saveStatus === 'saving'}
          className={`flex items-center gap-2 text-[10px] font-black px-5 py-2.5 rounded-xl transition-all shadow-sm ${
            saveStatus === 'saved'
              ? 'bg-emerald-500 text-white'
              : 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-800 hover:border-brand-500'
          }`}
        >
          {saveStatus === 'saving' ? (
            <div className="w-4 h-4 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"></div>
          ) : saveStatus === 'saved' ? (
            <CheckCircle2 size={16} />
          ) : (
            <Save size={16} />
          )}
          {saveStatus === 'saved' ? 'Synced to Vault' : 'Sync to Vault'}
        </button>
      </div>

      <div className="bg-brand-50 dark:bg-brand-900/10 p-6 rounded-[2rem] border border-brand-100 dark:border-brand-900/30 relative overflow-hidden transition-all duration-500">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-brand-500 font-black text-[10px] uppercase tracking-widest">
            <Sparkles size={14} className="animate-pulse" />
            <span>Intelligence Layer</span>
          </div>
          <button
            onClick={handleRunTests}
            disabled={isTesting}
            className="flex items-center gap-1.5 text-[9px] font-black text-slate-400 uppercase tracking-widest hover:text-brand-500 transition-colors"
          >
            <Activity size={12} className={isTesting ? 'animate-spin' : ''} />
            {isTesting ? 'Verifying...' : 'System Integrity Check'}
          </button>
        </div>

        {health && !isTesting && (
          <div className="mb-4 grid grid-cols-4 gap-2 animate-in fade-in slide-in-from-top-2">
            <div className="bg-white/50 dark:bg-slate-900/50 p-2 rounded-xl border border-slate-100 dark:border-slate-800 text-center">
              <p className="text-[7px] font-black text-slate-400 uppercase mb-1">Storage</p>
              <div
                className={`diagnostic-badge mx-auto ${health.storage === 'healthy' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'}`}
              >
                {health.storage === 'healthy' ? 'OK' : 'FAIL'}
              </div>
            </div>
            <div className="bg-white/50 dark:bg-slate-900/50 p-2 rounded-xl border border-slate-100 dark:border-slate-800 text-center">
              <p className="text-[7px] font-black text-slate-400 uppercase mb-1">Network</p>
              <div
                className={`diagnostic-badge mx-auto ${health.network === 'online' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-amber-500/10 text-amber-500'}`}
              >
                {health.network}
              </div>
            </div>
            <div className="bg-white/50 dark:bg-slate-900/50 p-2 rounded-xl border border-slate-100 dark:border-slate-800 text-center">
              <p className="text-[7px] font-black text-slate-400 uppercase mb-1">AI Link</p>
              <div
                className={`diagnostic-badge mx-auto ${health.aiEngine === 'ready' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'}`}
              >
                {health.aiEngine === 'ready'
                  ? 'READY'
                  : health.aiEngine === 'missing-key'
                    ? 'NO KEY'
                    : 'ERROR'}
              </div>
            </div>
            <div className="bg-white/50 dark:bg-slate-900/50 p-2 rounded-xl border border-slate-100 dark:border-slate-800 text-center">
              <p className="text-[7px] font-black text-slate-400 uppercase mb-1">Latent</p>
              <p className="text-[9px] font-black text-brand-500 leading-none h-4 flex items-center justify-center">
                {health.latency}ms
              </p>
            </div>
          </div>
        )}

        {health?.aiEngine === 'unreachable' && !isTesting && (
          <div className="mb-4 bg-red-500/10 border border-red-500/20 p-3 rounded-xl flex items-center gap-3 animate-in shake duration-500">
            <ShieldAlert size={16} className="text-red-500 shrink-0" />
            <p className="text-[10px] font-bold text-red-500 uppercase tracking-tight">
              AI Synthesis Unavailable: Handshake Failed. Verify API Credentials.
            </p>
          </div>
        )}

        {health?.aiEngine === 'missing-key' && !isTesting && (
          <div className="mb-4 bg-amber-500/10 border border-amber-500/20 p-3 rounded-xl flex items-start gap-3">
            <Info size={16} className="text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-[10px] font-bold text-amber-500 uppercase tracking-tight mb-1">
                API Key Not Configured
              </p>
              <p className="text-[9px] text-amber-400/80">
                Add{' '}
                <code className="bg-amber-500/20 px-1 rounded">VITE_OPENAI_API_KEY=your_key</code>{' '}
                to a <code className="bg-amber-500/20 px-1 rounded">.env</code> file in the project
                root to enable AI features.
              </p>
            </div>
          </div>
        )}

        {aiError && !isTesting && (
          <div className="mb-4 bg-red-500/10 border border-red-500/20 p-3 rounded-xl flex items-center gap-3">
            <AlertCircle size={16} className="text-red-500 shrink-0" />
            <p className="text-[10px] font-bold text-red-400">{aiError}</p>
            <button
              onClick={() => setAiError(null)}
              className="ml-auto text-red-400 hover:text-red-300"
            >
              <X size={14} />
            </button>
          </div>
        )}

        <textarea
          ref={aiInputRef}
          value={aiInputText}
          onChange={(e) => setAiInputText(e.target.value)}
          className="w-full p-4 text-sm rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-950/80 focus:border-brand-500 outline-none resize-none transition-all placeholder-slate-400 text-slate-900 dark:text-white min-h-[100px]"
          placeholder="e.g. 'Build an invoice for Acme: 5 hours of consulting at $200/hr...'"
          rows={3}
        />
        <div className="mt-4 flex items-center justify-between">
          <p className="text-[9px] text-slate-400">
            {health?.aiEngine === 'ready' ? (
              <span className="text-emerald-500">✓ AI Ready</span>
            ) : health?.aiEngine === 'missing-key' ? (
              <span className="text-amber-500">⚠ Configure API key</span>
            ) : (
              <span className="text-slate-500">Checking AI status...</span>
            )}
          </p>
          <button
            onClick={handleAIGenerate}
            disabled={isGenerating || !aiInputText.trim()}
            className="flex items-center gap-2 text-[10px] font-black bg-slate-900 dark:bg-brand-500 text-white dark:text-slate-950 px-6 py-3 rounded-xl disabled:opacity-40 transition-all uppercase tracking-wider hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-brand-500/10"
          >
            {isGenerating ? (
              <>
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Analyzing...
              </>
            ) : (
              'Execute Synthesis'
            )}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="label">Serial Reference</label>
          <div className="relative group">
            <Hash
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-brand-500"
              size={14}
            />
            <input
              type="text"
              placeholder="INV-2025-001"
              value={data.number}
              onChange={(e) => onUpdate({ number: e.target.value })}
              onBlur={(e) => validateField('number', e.target.value)}
              className={`input-field pl-12 ${errors.number ? 'border-red-500 focus:border-red-500' : ''}`}
            />
            {errors.number && (
              <span className="error-text">
                <AlertCircle size={10} /> {errors.number}
              </span>
            )}
          </div>
        </div>
        <div className="space-y-2">
          <label className="label">Lifecycle Status</label>
          <div className="relative">
            <select
              value={data.status}
              onChange={(e) => onUpdate({ status: e.target.value as any })}
              className="input-field appearance-none cursor-pointer"
            >
              <option value="Draft">Draft</option>
              <option value="Sent">Sent</option>
              <option value="Paid">Paid</option>
            </select>
            <ChevronDown
              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              size={14}
            />
          </div>
        </div>
        <div className="space-y-2">
          <label className="label">Issue Date</label>
          <input
            type="date"
            value={data.date}
            onChange={(e) => onUpdate({ date: e.target.value })}
            className="input-field"
          />
        </div>
        <div className="space-y-2">
          <label className="label">Maturity Date</label>
          <input
            type="date"
            value={data.dueDate}
            onChange={(e) => onUpdate({ dueDate: e.target.value })}
            className="input-field"
          />
        </div>
      </div>

      <Section title="Source Node (Issuer)" icon={<Briefcase size={20} />}>
        <div className="flex flex-col sm:flex-row gap-6">
          <div className="w-24 h-24 shrink-0 rounded-3xl bg-slate-50 dark:bg-slate-950 border-2 border-dashed border-slate-200 dark:border-slate-800 flex flex-col items-center justify-center text-slate-400 relative overflow-hidden group cursor-pointer hover:border-brand-500 transition-all">
            {data.logo ? (
              <>
                <img src={data.logo} className="w-full h-full object-contain p-2" alt="Logo" />
                <button
                  onClick={handleRemoveLogo}
                  className="absolute top-1 right-1 p-1 bg-white/80 dark:bg-slate-900/80 rounded-full text-red-500 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X size={12} />
                </button>
              </>
            ) : (
              <Upload size={24} />
            )}
            <input
              type="file"
              onChange={handleLogoUpload}
              className="absolute inset-0 opacity-0 cursor-pointer"
              accept="image/*"
            />
          </div>
          <div className="flex-1 space-y-4">
            <input
              type="text"
              placeholder="Your Professional Entity"
              value={data.senderName}
              onChange={(e) => onUpdate({ senderName: e.target.value })}
              onBlur={(e) => validateField('senderName', e.target.value)}
              className={`input-field font-black ${errors.senderName ? 'border-red-500 focus:border-red-500' : ''}`}
            />
            <textarea
              placeholder="Legal Address & Contact"
              value={data.senderAddress}
              onChange={(e) => onUpdate({ senderAddress: e.target.value })}
              className="input-field min-h-[80px]"
              rows={2}
            />
          </div>
        </div>
      </Section>

      <Section title="Destination Node (Recipient)" icon={<User size={20} />}>
        <div className="space-y-4">
          <input
            type="text"
            placeholder="Primary Contact Name"
            value={data.recipientName}
            onChange={(e) => onUpdate({ recipientName: e.target.value })}
            onBlur={(e) => validateField('recipientName', e.target.value)}
            className={`input-field font-black ${errors.recipientName ? 'border-red-500 focus:border-red-500' : ''}`}
          />
          <input
            type="text"
            placeholder="Organization / Legal Name"
            value={data.recipientCompany}
            onChange={(e) => onUpdate({ recipientCompany: e.target.value })}
            className="input-field"
          />
          <textarea
            placeholder="Registered Billing Address"
            value={data.recipientAddress}
            onChange={(e) => onUpdate({ recipientAddress: e.target.value })}
            className="input-field min-h-[80px]"
            rows={2}
          />
        </div>
      </Section>

      <Section title="Line Items" icon={<FileText size={20} />}>
        <div className="space-y-4">
          {data.items.map((item, index) => (
            <div
              key={item.id}
              className="flex flex-col sm:flex-row gap-4 items-start bg-slate-50 dark:bg-slate-950/50 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 group transition-all hover:bg-white dark:hover:bg-slate-900 shadow-sm hover:shadow-md"
            >
              <div className="flex-1 w-full space-y-2">
                <input
                  type="text"
                  placeholder="Service description..."
                  value={item.description}
                  onChange={(e) => updateItem(index, 'description', e.target.value)}
                  className="w-full bg-transparent border-none outline-none text-sm font-bold text-slate-900 dark:text-white placeholder-slate-400"
                />
                <div className="flex items-center gap-2 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                  <Layout size={10} /> Part #{index + 1}
                </div>
              </div>
              <div className="flex items-center gap-4 w-full sm:w-auto">
                <div className="flex flex-col items-center gap-1">
                  <span className="text-[8px] font-black text-slate-400 uppercase">Qty</span>
                  <input
                    type="number"
                    aria-label="Qty"
                    value={item.quantity}
                    onChange={(e) => updateItem(index, 'quantity', Number(e.target.value))}
                    className="w-16 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-800 p-2 rounded-lg text-center font-bold text-xs shadow-inner"
                  />
                </div>
                <div className="flex flex-col items-end gap-1 flex-1 sm:w-28">
                  <span className="text-[8px] font-black text-slate-400 uppercase">Rate</span>
                  <div className="relative w-full">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 font-black">
                      {settings.currencySymbol}
                    </span>
                    <input
                      type="number"
                      aria-label="Rate"
                      value={item.rate}
                      onChange={(e) => updateItem(index, 'rate', Number(e.target.value))}
                      className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-800 p-2 pl-6 rounded-lg text-right font-black text-xs shadow-inner"
                    />
                  </div>
                </div>
                <button
                  onClick={() => removeItem(index)}
                  className="mt-5 p-2.5 text-slate-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-xl transition-all"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
          <button
            onClick={addItem}
            className="w-full py-5 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl text-slate-400 hover:text-brand-500 hover:border-brand-500 transition-all flex items-center justify-center gap-3 font-black text-[10px] uppercase tracking-[0.2em] group"
          >
            <Plus size={18} className="group-hover:rotate-90 transition-transform" /> Add Document
            Entry
          </button>
        </div>
      </Section>

      <Section title="Metadata & Logistics" icon={<Layout size={20} />}>
        <div className="space-y-6">
          <div className="space-y-2">
            <label className="label">Extra Notes</label>
            <textarea
              placeholder="Project milestones..."
              value={data.notes}
              onChange={(e) => onUpdate({ notes: e.target.value })}
              className="input-field min-h-[80px]"
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <label className="label">Payment Terms</label>
            <textarea
              placeholder="e.g. Net 14..."
              value={data.paymentInfo}
              onChange={(e) => onUpdate({ paymentInfo: e.target.value })}
              className="input-field min-h-[80px]"
              rows={2}
            />
          </div>
        </div>
      </Section>

      <style>{`
        .label {
          @apply block text-[10px] font-black text-slate-500 mb-1 uppercase tracking-widest px-1;
        }
        .input-field {
          @apply w-full px-4 py-3.5 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-900 dark:text-white placeholder-slate-400 focus:ring-4 focus:ring-brand-500/5 focus:border-brand-500 outline-none text-sm transition-all shadow-sm;
        }
        .error-text {
          @apply text-[9px] font-bold text-red-500 flex items-center gap-1 mt-1 px-2;
        }
      `}</style>
    </div>
  );
};

export default InvoiceForm;
